import type { WsMessage } from "@/api/websocket";
import { recordContextualTaskAcceptance } from "@/desktop/onboarding/contextual-task-acceptance";
import {
  bindContextualDiscoveryExecution,
  deferContextualQuestionSpeech,
  isContextualDiscoveryPresentationHeld,
} from "@/desktop/onboarding/contextual-discovery-presentation";
import { getOutboundScreensForContext } from "@/desktop/outbound-screenshot-metadata";
import { asActivityLabel } from "@/lib/activity-label";
import { extractPointToken, stripPointTokens } from "@/lib/agent-pointing";
import { desktopApprovalFields } from "@/lib/desktop-approval-question";
import { normalizeAssistantText } from "@/lib/assistant-text-normalization";
import { questionSpeechText } from "@/lib/question-speech-text";
import { resolveAgentPoint } from "@/lib/pointing-transform";
import { refreshSoundElsewhere } from "@/desktop/tts-quiet-turns";
import { retainTerminalCursor } from "./terminal-cursor-lifetime";

import {
  displayIdFromDesktopPayload,
  stringArray,
} from "./companion-frame-reducer-frames";
import { logTtsPipelineStreamStart } from "@/desktop/tts-pipeline-log";
import type { FrameApplyContext } from "./companion-frame-reducer-shared";
import {
  currentCursorDisplayId,
  markConversationToolFailed,
  pushConversationToolCall,
  sessionIdForExecution,
  visibleConversationFrameForExecution,
} from "./companion-frame-reducer-shared";
export const awaitingInputTtsSource = (awaitingInputId: string) =>
  `awaiting-input:${awaitingInputId}`;

export function applyCursorEvent(msg: WsMessage, ctx: FrameApplyContext): void {
  bindContextualDiscoveryExecution(msg.data);
  if (msg.type === "cursor.move") {
    const d = msg.data as Record<string, unknown>;
    const agentId = (d.agent_id ?? d.agentId) as string;
    const projectState = ctx.projectStore.getState();
    const currentProjectId = projectState.currentProject?.id;
    const currentSpaceId = projectState.currentSpace?.id;
    const cursorProjectId = d.project_id as string | undefined;
    const cursorSpaceId =
      cursorProjectId ??
      (d.space_id as string | undefined) ??
      currentProjectId ??
      currentSpaceId;
    if (!cursorSpaceId) {
      console.warn("[cursor.move] missing space_id, dropping cursor");
      return;
    }
    if (!agentId) return;
    const cursorExecutionId = d.execution_id as string | undefined;
    // Whether another app plays sound or has the microphone, for the turn's speech (`tts-quiet-turns.ts`).
    refreshSoundElsewhere();
    const currentCursor = ctx.canvasStore.getState().agentCursors.get(agentId);
    // A late result may update history, but must not revive an older run or
    // cancel the current cursor's terminal expiry timer.
    if (
      d.tool_completed === true &&
      currentCursor?.executionId &&
      cursorExecutionId &&
      currentCursor.executionId !== cursorExecutionId
    ) {
      if (d.tool_failed === true) {
        markConversationToolFailed({
          ctx,
          executionId: cursorExecutionId,
          toolUseId:
            typeof d.tool_use_id === "string" ? d.tool_use_id : undefined,
        });
      }
      return;
    }
    const pending = ctx.cursorRemovalTimers.get(agentId);
    if (pending) {
      clearTimeout(pending);
      ctx.cursorRemovalTimers.delete(agentId);
    }
    const cursorSessionId = sessionIdForExecution(
      ctx,
      cursorExecutionId,
      d.runtime_session_id as string | undefined,
    );
    if (cursorExecutionId) {
      ctx.companionStore.getState().restoreAgentResponding(cursorExecutionId);
    }
    const nonBlank = (v: unknown): string | undefined =>
      typeof v === "string" && v.trim().length > 0 ? v : undefined;
    const hasTargetFrameField = "target_frame_id" in d || "targetFrameId" in d;
    const targetFrameId = nonBlank(d.target_frame_id ?? d.targetFrameId);
    const toolCall = nonBlank(d.tool_call);
    const toolCallLabel = toolCall
      ? asActivityLabel(d.tool_call_label)
      : undefined;
    const rawContent = nonBlank(d.content);
    const rawContentFull = nonBlank(d.content_full);
    const originFromCursor = d.origin as "chat" | "heartbeat" | undefined;
    const displayId = displayIdFromDesktopPayload(d);
    const pointAt = extractPointToken(rawContentFull ?? rawContent);
    const displayContent = nonBlank(
      normalizeOptionalAssistantText(stripPointTokens(rawContent)),
    );
    const displayContentFull = nonBlank(
      normalizeOptionalAssistantText(stripPointTokens(rawContentFull)),
    );
    const hintedFrameId = nonBlank(d.conversation_frame_id);
    const visibleConversationFrameId = visibleConversationFrameForExecution(
      ctx,
      cursorExecutionId,
      cursorSessionId,
      hintedFrameId,
    );
    const resolvedPoint = resolveAgentPoint(
      pointAt,
      getOutboundScreensForContext({
        executionId: cursorExecutionId,
        sessionId: cursorSessionId,
        imageIds: stringArray(d.image_ids),
      }),
    );
    const cursorToolName = nonBlank(d.tool_name);
    const cursorToolFailed = d.tool_failed === true;
    const completedToolUseIds =
      d.tool_completed === true
        ? (stringArray(d.tool_completed_ids) ??
          [nonBlank(d.tool_use_id)].filter((id): id is string => !!id))
        : undefined;
    const failedToolUseId = nonBlank(d.tool_use_id);
    const currentToolUseId = currentCursor?.toolUseId;
    if (cursorToolFailed && !toolCall) {
      // A tool_result error arrives without a fresh label — the friendly
      // label is already on the matching tool step, so only flag it failed.
      markConversationToolFailed({
        ctx,
        executionId: cursorExecutionId,
        toolUseId: nonBlank(d.tool_use_id),
      });
    }
    pushConversationToolCall({
      ctx,
      executionId: cursorExecutionId,
      sessionId: cursorSessionId,
      conversationFrameId: visibleConversationFrameId,
      toolCall,
      toolCallLabel,
      toolFailed: toolCall && cursorToolFailed ? true : undefined,
      toolUseId: nonBlank(d.tool_use_id),
      actionSeq: d.action_seq ?? d.actionSeq,
      timestamp: msg.timestamp,
    });
    // Always retain the session cursor as the fallback reply surface. The
    // cursor layer suppresses only the duplicate UI for an exact focused chat
    // session, so closing that frame can reveal the live stream immediately.
    ctx.canvasStore.getState().setAgentCursor({
      agentId,
      spaceId: cursorSpaceId,
      ...(cursorProjectId ? { projectId: cursorProjectId } : {}),
      ...(cursorExecutionId ? { executionId: cursorExecutionId } : {}),
      ...(cursorSessionId ? { sessionId: cursorSessionId } : {}),
      ...(hintedFrameId ? { conversationFrameId: hintedFrameId } : {}),
      name: (d.name as string) ?? "Agent",
      color: (d.color as string) ?? "var(--color-accent)",
      toolCall,
      toolCallLabel,
      toolName: cursorToolName,
      ...(toolCall ? { toolUseId: nonBlank(d.tool_use_id) } : {}),
      ...(completedToolUseIds?.length ? { completedToolUseIds } : {}),
      ...(d.reads_only === true ? { readsOnly: true as const } : {}),
      ...(cursorToolFailed && (toolCall || failedToolUseId === currentToolUseId)
        ? { toolFailed: true as const }
        : {}),
      content: displayContent,
      contentFull: displayContentFull,
      ...(resolvedPoint
        ? {
            pointTarget: {
              displayId: resolvedPoint.displayId,
              globalX: resolvedPoint.globalX,
              globalY: resolvedPoint.globalY,
              seq: Date.now(),
            },
          }
        : {}),
      ...(hasTargetFrameField ? { targetFrameId: targetFrameId ?? null } : {}),
      ...("task_id" in d || "taskId" in d
        ? { taskId: (d.task_id ?? d.taskId ?? null) as string | null }
        : {}),
      ...("action_seq" in d || "actionSeq" in d
        ? { actionSeq: (d.action_seq ?? d.actionSeq) as number }
        : {}),
      ...(originFromCursor ? { origin: originFromCursor } : {}),
      ...(displayId !== null ? { displayId } : {}),
      lastSeen: new Date().toISOString(),
    });
    retainTerminalCursor(ctx, agentId, cursorExecutionId);
    const streamedContent = nonBlank(
      normalizeOptionalAssistantText(
        stripPointTokens(rawContentFull ?? rawContent),
      ),
    );
    const isStatusContent =
      streamedContent?.trim().toLowerCase() === "waking up...";
    const isAssistantStream = d.assistant_streaming !== false;
    if (
      cursorExecutionId &&
      streamedContent &&
      !isStatusContent &&
      isAssistantStream
    ) {
      logTtsPipelineStreamStart(
        "visible_stream_text",
        cursorExecutionId,
        streamedContent.length,
      );
      const streamStepId = nonBlank(d.step_id);
      ctx.conversationStore
        .getState()
        .upsertStreamingAssistantMessage(
          cursorExecutionId,
          streamedContent,
          msg.timestamp,
          {
            stepId: streamStepId,
            sessionId: cursorSessionId,
            // Bind only to the validated visible frame. A stale server hint
            // must not hide the live reply on a missing frame or override the
            // session/execution fallback resolved above.
            conversationFrameId: visibleConversationFrameId ?? undefined,
          },
        );
      const ttsOrigin =
        originFromCursor === "heartbeat" ||
        ctx.companionStore.getState().executions.get(cursorExecutionId)
          ?.origin === "heartbeat"
          ? "heartbeat"
          : "chat";
      // Generic MCP/status cursors omit this provenance flag. They may remain
      // visible, but only backend-authenticated CLI assistant streams can
      // trigger the early spoken sentence.
      if (
        d.assistant_streaming === true &&
        !isContextualDiscoveryPresentationHeld(cursorExecutionId)
      ) {
        ctx.ttsStore
          .getState()
          .speakStreamingReply(
            cursorExecutionId,
            streamedContent,
            ttsOrigin,
            streamStepId,
          );
      }
    }
    return;
  }

  // cursor.question — guard against unknown future cursor.* subtypes
  if (msg.type !== "cursor.question") return;
  const d = msg.data as Record<string, unknown>;
  const cursorProjectId = d.project_id as string | undefined;
  // Read live scope from store — the WS closure may hold a stale captured
  // value. Mirror cursor.move's resolution (no exact-match gate): a
  // question from an agent on a project the user isn't currently viewing
  // must still land on its AgentCursor so the chat's inline question card
  // and the cursor's "?" badge find it once that project is open. The
  // on-canvas cursor already filters by project at render time in
  // agent-cursor-layer.tsx, which is the correct layer for that filter.
  const projectState = ctx.projectStore.getState();
  const cursorSpaceId =
    cursorProjectId ??
    (d.space_id as string | undefined) ??
    projectState.currentProject?.id ??
    projectState.currentSpace?.id;
  if (!cursorSpaceId) {
    console.warn("[cursor.question] missing scope, dropping question");
    return;
  }
  const incomingAgentId = (d.agent_id ?? d.agentId) as string | undefined;
  if (!incomingAgentId) return;
  const incomingName = (d.name as string) ?? (d.agent_key as string);
  const incomingExecutionId = d.execution_id as string | undefined;

  // Match by name to avoid divergent cursor keys when session FK materialises late.
  let agentId = incomingAgentId;
  if (incomingName) {
    const lower = incomingName.split(" · ")[0]!.toLowerCase();
    for (const [id, c] of ctx.canvasStore.getState().agentCursors) {
      if (
        c.spaceId === cursorSpaceId &&
        (!incomingExecutionId ||
          !c.executionId ||
          c.executionId === incomingExecutionId) &&
        c.name &&
        c.name.split(" · ")[0]!.toLowerCase() === lower
      ) {
        agentId = id;
        break;
      }
    }
  }

  const pending = ctx.cursorRemovalTimers.get(agentId);
  if (pending) {
    clearTimeout(pending);
    ctx.cursorRemovalTimers.delete(agentId);
  }
  const awaitingInputId = d.awaiting_input_id as string;
  const questionText = (d.question as string) ?? "";
  const questionOptions =
    (d.options as Array<{ id: string; label: string }>) ?? [];
  // Whether this cursor was already holding this same question. A question can
  // arrive twice - a reconnect - and the user should hear it once.
  const asking = ctx.canvasStore.getState().agentCursors.get(agentId);
  const alreadyAsked = asking?.question?.awaitingInputId === awaitingInputId;
  // A desktop approval asks at its target, else where the agent last acted
  // (the rest point a question drops; a repeat keeps the first point).
  const approval = desktopApprovalFields(d);
  const approvalPoint = approval.kind
    ? (approval.desktopTarget ??
      (alreadyAsked ? asking?.question?.desktopTarget : asking?.restPoint))
    : undefined;
  ctx.canvasStore.getState().setAgentCursorQuestion(
    agentId,
    {
      awaitingInputId,
      question: questionText,
      options: questionOptions,
      ...approval,
      ...(approvalPoint ? { desktopTarget: approvalPoint } : {}),
    },
    {
      spaceId: cursorSpaceId,
      name: (d.name as string) ?? (d.agent_key as string) ?? "agent",
      color: (d.color as string) ?? "var(--color-accent)",
    },
  );
  // Say it out loud. A question is the one thing the agent produces that the
  // user has to act on before anything else can happen, and on the desktop the
  // card can be behind whatever they are working in. Include each choice so
  // listening users can answer without finding the card.
  //
  // Contexts without speech hold `silentTtsStore`, so this is a no-op there.
  // Not for a background run: heartbeat agents are silent, and a question from
  // one arriving out loud with no conversation on screen startles rather than
  // helps. The card still shows it.
  const background =
    typeof d.execution_id === "string" &&
    ctx.companionStore.getState().executions.get(d.execution_id)?.origin ===
      "heartbeat";
  const deferredSpeech = deferContextualQuestionSpeech(
    typeof d.execution_id === "string" ? d.execution_id : undefined,
    awaitingInputId,
  );
  if (!alreadyAsked && !background && !deferredSpeech && questionText.trim()) {
    ctx.ttsStore
      .getState()
      .speak(questionSpeechText(questionText, questionOptions), null, {
        ...(typeof d.execution_id === "string"
          ? { executionId: d.execution_id }
          : {}),
        ttsSource: awaitingInputTtsSource(awaitingInputId),
      });
  }
  const questionCursor = ctx.canvasStore.getState().agentCursors.get(agentId);
  if (questionCursor)
    ctx.canvasStore.getState().setAgentCursor({
      agentId: questionCursor.agentId,
      spaceId: questionCursor.spaceId,
      ...(cursorProjectId ? { projectId: cursorProjectId } : {}),
      ...(typeof d.execution_id === "string"
        ? { executionId: d.execution_id }
        : {}),
      name: questionCursor.name,
      color: questionCursor.color,
      targetFrameId: questionCursor.targetFrameId,
      taskId: questionCursor.taskId,
      displayId: approvalPoint?.displayId ?? currentCursorDisplayId(),
      lastSeen: new Date().toISOString(),
    });
}

export function normalizeOptionalAssistantText(
  text: string | undefined,
): string | undefined {
  return text === undefined ? undefined : normalizeAssistantText(text);
}

export function applyAwaitingInputEvent(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  if (msg.type === "awaiting_input.requested") {
    const d = msg.data as Record<string, unknown>;
    ctx.canvasStore.getState().addAwaitingInput({
      id: d.awaiting_input_id as string,
      frameId: d.frame_id as string,
      executionId: d.execution_id as string,
      projectId: typeof d.project_id === "string" ? d.project_id : undefined,
      conversationId:
        typeof d.conversation_id === "string" ? d.conversation_id : null,
      taskId: typeof d.task_id === "string" ? d.task_id : null,
      question: (d.question as string) ?? "",
      context: (d.context as string) ?? "",
      options: (d.options as { id: string; label: string }[]) ?? [],
      createdAt: (d.created_at as string) ?? new Date().toISOString(),
      timeoutAt: (d.timeout_at as string) ?? new Date().toISOString(),
    });
    return;
  }
  // awaiting_input.resolved / awaiting_input.answered — guard against unknown future subtypes
  if (
    msg.type !== "awaiting_input.resolved" &&
    msg.type !== "awaiting_input.answered"
  )
    return;
  const aid = msg.data.awaiting_input_id as string;
  recordContextualTaskAcceptance(msg.data);
  const executionId = msg.data.execution_id as string | undefined;
  if (executionId) ctx.ttsStore.getState().resumeAnsweredTurn(executionId);
  // Answered, possibly on another device: stop reading the question out.
  ctx.ttsStore.getState().cancelPresentationSpeech(awaitingInputTtsSource(aid));
  ctx.canvasStore.getState().removeAwaitingInput(aid);
  for (const [agentId, cursor] of ctx.canvasStore.getState().agentCursors) {
    if (cursor.question?.awaitingInputId === aid) {
      ctx.canvasStore.getState().clearAgentCursorQuestion(agentId);
      clearTimeout(ctx.cursorRemovalTimers.get(agentId));
      const timer = ctx.defer(() => {
        ctx.canvasStore.getState().removeAgentCursor(agentId);
        ctx.cursorRemovalTimers.delete(agentId);
      }, 30_000);
      ctx.cursorRemovalTimers.set(agentId, timer);
    }
  }
}
